import React, { useState } from 'react'
import { AdminUser, DEPARTMENTS, PRIORITY_LABELS } from './core/_models'
import { createTicketByAdmin } from './core/_requests'

interface CreateTicketModalProps {
  show: boolean
  onClose: () => void
  onCreated: (ticketId: string) => void
  admins: AdminUser[]
}

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({ show, onClose, onCreated, admins }) => {
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [department, setDepartment] = useState('')
  const [priority, setPriority] = useState('medium')
  const [assignedTo, setAssignedTo] = useState('')
  const [accountSearch, setAccountSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!subject.trim()) {
      setError('נדרש נושא לפנייה')
      return
    }

    setCreating(true)
    setError('')
    try {
      const data: any = {
        subject: subject.trim(),
        priority,
        is_internal: isInternal,
      }
      if (message.trim()) data.message = message.trim()
      if (department) data.department = department
      if (assignedTo) data.assigned_to = assignedTo

      const res = await createTicketByAdmin(data)
      onCreated(res.data.ticket._id)
      resetForm()
    } catch (err: any) {
      setError(err.response?.data?.error || 'שגיאה ביצירת הפנייה')
    } finally {
      setCreating(false)
    }
  }

  const resetForm = () => {
    setSubject('')
    setMessage('')
    setIsInternal(false)
    setDepartment('')
    setPriority('medium')
    setAssignedTo('')
    setAccountSearch('')
    setError('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  // Filter admins by selected department
  const filteredAdmins = department
    ? admins.filter(a => a.departments?.includes(department))
    : admins

  if (!show) return null

  return (
    <div className="tag-manager-overlay" onClick={handleClose}>
      <div className="create-ticket-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>פנייה חדשה</h3>
          <button className="close-btn" onClick={handleClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-msg">{error}</div>
          )}

          {/* Ticket Type Toggle */}
          <div className="form-row">
            <div className="type-toggle">
              <button
                className={`type-btn ${!isInternal ? 'active' : ''}`}
                onClick={() => setIsInternal(false)}
              >
                <i className="bi bi-person"></i>
                פנייה ללקוח
              </button>
              <button
                className={`type-btn internal ${isInternal ? 'active' : ''}`}
                onClick={() => setIsInternal(true)}
              >
                <i className="bi bi-building"></i>
                פנייה פנימית
              </button>
            </div>
          </div>

          {/* Subject */}
          <div className="form-field">
            <label>נושא *</label>
            <input
              type="text"
              placeholder="נושא הפנייה..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
            />
          </div>

          {/* Department */}
          <div className="form-row">
            <div className="form-field">
              <label>מחלקה</label>
              <select value={department} onChange={(e) => {
                setDepartment(e.target.value)
                setAssignedTo('') // reset assignee when department changes
              }}>
                <option value="">ללא מחלקה</option>
                {DEPARTMENTS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>עדיפות</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}>
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Assignee */}
          <div className="form-field">
            <label>
              שיוך ל
              {department && (
                <span className="dept-hint"> (מסונן לפי {DEPARTMENTS.find(d => d.value === department)?.label})</span>
              )}
            </label>
            <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
              <option value="">לא משויך</option>
              {filteredAdmins.map(admin => (
                <option key={admin._id} value={admin._id}>{admin.username}</option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div className="form-field">
            <label>הודעה ראשונית</label>
            <textarea
              placeholder={isInternal ? 'תיאור הבעיה / משימה...' : 'הודעה ללקוח...'}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button
              className="btn-discord"
              onClick={handleSubmit}
              disabled={creating || !subject.trim()}
            >
              {creating ? (
                <><div className="spinner" style={{ width: 16, height: 16 }}></div> יוצר...</>
              ) : (
                <><i className="bi bi-plus-lg"></i> צור פנייה</>
              )}
            </button>
            <button className="btn-discord secondary" onClick={handleClose}>
              ביטול
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateTicketModal
